import { Children, cloneElement, isValidElement, useCallback, useState } from "react";
import type { ReactElement } from "react";

export interface PanelColumnProps {
  x?: number;
  y: number;
  z?: number;
  gap?: number;
  children: React.ReactNode;
}

// Stacks PanelText children top-to-bottom using each child's REAL measured
// height (troika's onSync -> textRenderInfo.blockBounds), never an
// estimated character count. An estimate calibrated today would drift the
// moment content length changes or fonts.ts's kill switch is lifted and the
// real Zen metrics land — measured bounds track both for free. Pass
// key={someContentIdentity} at the call site so a full content swap (e.g.
// paging to a new onboarding screen) resets to a clean re-measure instead
// of interpolating between two different texts' heights.
//
// Every child must be a <PanelText anchorX="left" .../> — this overrides
// anchorY to "top" on each so blockBounds' top edge lands exactly at the
// cursor Y (see troika-three-text: blockBounds is already anchor-adjusted,
// so anchorY="top" puts local y=0 at the block's top and -height at its
// bottom).
export function PanelColumn({ x = 0, y, z = 0, gap = 0.02, children }: PanelColumnProps) {
  const items = Children.toArray(children).filter(isValidElement) as ReactElement<any>[];
  const [heights, setHeights] = useState<number[]>([]);

  const setHeightAt = useCallback((index: number, h: number) => {
    setHeights((prev) => {
      if (Math.abs((prev[index] ?? -1) - h) < 0.001) return prev;
      const next = prev.slice();
      next[index] = h;
      return next;
    });
  }, []);

  let cursorY = y;
  return (
    <>
      {items.map((child, index) => {
        const childY = cursorY;
        const measured = heights[index] ?? 0;
        cursorY = childY - measured - gap;
        const childOnSync = child.props.onSync as ((troika: unknown) => void) | undefined;
        return cloneElement(child, {
          key: child.key ?? index,
          position: [x, childY, z],
          anchorY: "top",
          onSync: (troika: { textRenderInfo?: { blockBounds?: number[] } }) => {
            const bounds = troika?.textRenderInfo?.blockBounds;
            if (bounds) setHeightAt(index, Math.max(0, bounds[3] - bounds[1]));
            childOnSync?.(troika);
          },
        });
      })}
    </>
  );
}
